// SPDX-License-Identifier: AGPL-3.0-only
/**
 * Intl formatting layer. All numbers, dates, currencies, relative times,
 * and lists render through here — never through hand-rolled helpers or
 * `toLocaleString('en-US')`.
 *
 * Numeral policy (normative): `ctx: "data"` (the default) forces Latin
 * digits (`-u-nu-latn`) and the gregorian calendar (`-ca-gregory`) so mono
 * data cells stay `tabular-nums`-aligned in every locale, including `ar_EG`;
 * `ctx: "prose"` uses the locale's default numbering system, so Arabic prose
 * renders Arabic-Indic digits. `Intl.*` instances are expensive — memoized
 * per `(tag, options, context)`.
 */
const WEEK_INFO_FALLBACK = {
    'en-US': { firstDay: 7, weekend: [6, 7] },
    'de-DE': { firstDay: 1, weekend: [6, 7] },
    'fr-FR': { firstDay: 1, weekend: [6, 7] },
    'cs-CZ': { firstDay: 1, weekend: [6, 7] },
    'da-DK': { firstDay: 1, weekend: [6, 7] },
    'zh-CN': { firstDay: 1, weekend: [6, 7] },
    'zh-TW': { firstDay: 1, weekend: [6, 7] },
    'ar-EG': { firstDay: 6, weekend: [5, 6] },
};
/** `Intl.Locale#getWeekInfo()` where available, else the static table. */
export function weekInfo(tag) {
    try {
        const locale = new Intl.Locale(tag);
        const info = locale.getWeekInfo?.() ?? locale.weekInfo;
        if (info !== undefined)
            return { firstDay: info.firstDay, weekend: info.weekend };
    }
    catch {
        // Fall through to the static table.
    }
    return WEEK_INFO_FALLBACK[tag] ?? { firstDay: 1, weekend: [6, 7] };
}
/** Latn digits + gregorian calendar pinned for data context. */
function tagFor(tag, ctx) {
    // Coalesce empty/invalid tags (see `normalizeTag`) before appending the
    // `-u-...` extension, so `latnDataTag('')` and direct `Intl.*` builds off it
    // don't produce an invalid `"-u-nu-latn-…"` tag that throws.
    const base = normalizeTag(tag);
    if (ctx === 'prose')
        return base;
    return base.includes('-u-') ? `${base}-nu-latn-ca-gregory` : `${base}-u-nu-latn-ca-gregory`;
}
/**
 * The data-context BCP-47 tag (latn digits + gregorian calendar) for
 * callers that must build an `Intl.*` instance this layer does not expose —
 * e.g. a narrow-style relative-time formatter for a mono grid cell. Keeps
 * the
 * numeral policy in one place instead of hand-rolling the `-u-nu-latn` suffix.
 */
export function latnDataTag(tag) {
    return tagFor(tag, 'data');
}
const numberFormats = new Map();
const dateFormats = new Map();
const listFormats = new Map();
const relativeFormats = new Map();
const collators = new Map();
function numberFormat(tag, ctx, options) {
    const resolved = tagFor(tag, ctx);
    const key = `${resolved}|${JSON.stringify(options)}`;
    let fmt = numberFormats.get(key);
    if (fmt === undefined) {
        fmt = new Intl.NumberFormat(resolved, options);
        numberFormats.set(key, fmt);
    }
    return fmt;
}
function dateFormat(tag, ctx, options) {
    const resolved = tagFor(tag, ctx);
    const key = `${resolved}|${JSON.stringify(options)}`;
    let fmt = dateFormats.get(key);
    if (fmt === undefined) {
        fmt = new Intl.DateTimeFormat(resolved, options);
        dateFormats.set(key, fmt);
    }
    return fmt;
}
function toDate(d) {
    return d instanceof Date ? d : new Date(d);
}
const BYTE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte', 'petabyte'];
const DATE_STYLES = {
    short: { dateStyle: 'short' },
    medium: { dateStyle: 'medium' },
    long: { dateStyle: 'long' },
};
const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;
/**
 * `Intl.NumberFormat.prototype.format` accepts a numeric STRING at runtime —
 * Intl.NumberFormat v3, shipped in every engine this product supports and
 * verified here on Node 22 for the number, percent and currency styles. The
 * TypeScript lib has not caught up: its three overloads stop at
 * `number | bigint`. This cast is exactly that gap and nothing wider; passing
 * a string is the whole point (see {@link Numeric}).
 */
function formatNumeric(format, value) {
    return format.format(value);
}
const formattersByTag = new Map();
function buildFormatters(tag) {
    const relativeFor = (t) => {
        let fmt = relativeFormats.get(t);
        if (fmt === undefined) {
            fmt = new Intl.RelativeTimeFormat(t, { numeric: 'auto' });
            relativeFormats.set(t, fmt);
        }
        return fmt;
    };
    const fmt = {
        number(v, o = {}) {
            const { ctx = 'data', ...options } = o;
            return formatNumeric(numberFormat(tag, ctx, options), v);
        },
        compact(v, o = {}) {
            return numberFormat(tag, o.ctx ?? 'data', { notation: 'compact', maximumFractionDigits: 1 }).format(v);
        },
        percent(v, o = {}) {
            const digits = o.fractionDigits ?? 0;
            return formatNumeric(numberFormat(tag, o.ctx ?? 'data', {
                style: 'percent',
                minimumFractionDigits: digits,
                maximumFractionDigits: digits,
            }), v);
        },
        currency(v, currency, o = {}) {
            return formatNumeric(numberFormat(tag, o.ctx ?? 'data', { style: 'currency', currency }), v);
        },
        bytes(v) {
            const magnitude = v === 0 ? 0 : Math.floor(Math.log10(Math.abs(v)) / 3);
            const index = Math.min(Math.max(magnitude, 0), BYTE_UNITS.length - 1);
            const unit = BYTE_UNITS[index] ?? 'byte';
            return numberFormat(tag, 'data', {
                style: 'unit',
                unit,
                unitDisplay: 'short',
                maximumFractionDigits: 1,
            }).format(v / 1000 ** index);
        },
        date(d, style = 'medium', o = {}) {
            return dateFormat(tag, o.ctx ?? 'data', {
                ...DATE_STYLES[style],
                ...(o.timeZone === undefined ? {} : { timeZone: o.timeZone }),
            }).format(toDate(d));
        },
        time(d, o = {}) {
            return dateFormat(tag, o.ctx ?? 'data', {
                timeStyle: 'short',
                ...(o.timeZone === undefined ? {} : { timeZone: o.timeZone }),
            }).format(toDate(d));
        },
        dateTime(d, o = {}) {
            return dateFormat(tag, o.ctx ?? 'data', {
                dateStyle: 'medium',
                timeStyle: 'short',
                ...(o.timeZone === undefined ? {} : { timeZone: o.timeZone }),
            }).format(toDate(d));
        },
        dateRange(a, b) {
            return dateFormat(tag, 'data', { dateStyle: 'medium' }).formatRange(toDate(a), toDate(b));
        },
        relative(d, o = {}) {
            const now = o.now ?? Date.now();
            const delta = toDate(d).getTime() - now;
            const abs = Math.abs(delta);
            const rtf = relativeFor(tag); // prose context: relative times live in sentences
            if (abs < 45_000)
                return rtf.format(0, 'second'); // "now" via numeric: 'auto'
            if (abs < 90 * MINUTE)
                return rtf.format(Math.round(delta / MINUTE), 'minute');
            if (abs < 22 * HOUR)
                return rtf.format(Math.round(delta / HOUR), 'hour');
            if (abs < 26 * DAY)
                return rtf.format(Math.round(delta / DAY), 'day');
            return fmt.date(d);
        },
        list(items, type = 'conjunction') {
            const key = `${tag}|${type}`;
            let lf = listFormats.get(key);
            if (lf === undefined) {
                lf = new Intl.ListFormat(tag, { type });
                listFormats.set(key, lf);
            }
            return lf.format(items);
        },
        collator() {
            let c = collators.get(tag);
            if (c === undefined) {
                c = new Intl.Collator(tag, { numeric: true });
                collators.set(tag, c);
            }
            return c;
        },
    };
    return fmt;
}
/**
 * Coalesce an empty/whitespace/structurally-invalid tag to `en-US`. Callers
 * often pass `config.locale ?? 'en-US'`, which lets an empty string `''` slip
 * through (`??` only catches null/undefined) — and `new Intl.NumberFormat('')`
 * throws `RangeError`. Normalizing here makes every formatter safe against a
 * schema-valid empty locale, so no widget crashes on it.
 */
function normalizeTag(tag) {
    if (typeof tag !== 'string' || tag.trim() === '')
        return 'en-US';
    try {
        Intl.getCanonicalLocales(tag); // throws RangeError on an invalid tag
        return tag;
    }
    catch {
        return 'en-US';
    }
}
/** Memoized per BCP-47 tag. Framework-free — the React binding is `useFmt()` (./react.js entry). */
export function getFormatters(tag) {
    const normalized = normalizeTag(tag);
    let fmt = formattersByTag.get(normalized);
    if (fmt === undefined) {
        fmt = buildFormatters(normalized);
        formattersByTag.set(normalized, fmt);
    }
    return fmt;
}
//# sourceMappingURL=index.js.map